/**
 * Pure pipeline-progress math for the finalize timeline — no React, no I/O.
 *
 * Extracted verbatim from the meeting-detail route so the meetings LIST can
 * compute the same per-card progress from the same SSE stream. The detail page
 * drives the finalize "wow" screen from these helpers; the math here must stay
 * identical to what it computed inline.
 */
import { stepStage, stepWeight } from "./pipeline-steps"

/**
 * Coarse finalize stage shown on the timeline. This is the canonical home for
 * the union — `FinalizationProgress` re-exports it for its many consumers, so
 * moving it here keeps every existing `import ... from ".../FinalizationProgress"`
 * working while breaking the component→lib layering that a shared home requires.
 */
export type TProcessingStage = "uploading" | "transcribing" | "identifying_speakers" | "analyzing" | "complete"

/** Plain string list (not the union) so `.includes(s: string)` type-checks — the
 * same shape the detail page's inline `isValidStage` guard used. */
const PROCESSING_STAGE_NAMES: readonly string[] = [
  "uploading",
  "transcribing",
  "identifying_speakers",
  "analyzing",
  "complete",
]

/** Narrows a raw string (e.g. the persisted `processing_stage` column) to a stage. */
export function isProcessingStage(s: string): s is TProcessingStage {
  return PROCESSING_STAGE_NAMES.includes(s)
}

/**
 * Weighted, skip-excluded finalize progress accumulator. Weight ≈ wall-clock
 * cost (see `pipeline-steps`), so the long `final-analysis` summary dominates
 * the bar and the instantly-skipped Voxcanon branch barely moves it.
 */
export interface IPipelineWeights {
  totalWeight: number
  completedWeight: number
  skippedWeight: number
  /** Weight of the step currently running — credited at 50% so the bar advances
   * when a heavy step (the summary) starts, then creeps. */
  runningWeight: number
  runningName: string | null
  runningStage: TProcessingStage | null
}

/** The render-ready progress a card/timeline consumes. */
export interface IMeetingCardProcessing {
  stage: TProcessingStage | null
  progress: number
  currentStepName: string | null
}

/** Coarse per-stage progress floor — an active stage lifts the bar to at least
 * this without waiting for the next weighted checkpoint or poll. */
export const STAGE_FLOOR: Record<string, number> = { identifying_speakers: 30, analyzing: 60, complete: 100 }

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null
}

/**
 * Handle a `PIPELINE_STARTED` roster payload: sum each instance's step weight
 * into the denominator and reset the accumulators.
 */
export function pipelineStarted(payload: Record<string, unknown>): IPipelineWeights {
  const instances: unknown[] = Array.isArray(payload.instances) ? payload.instances : []
  const totalWeight = instances.reduce<number>((sum, inst) => {
    const ref = isRecord(inst) && typeof inst.ref === "string" ? inst.ref : ""
    return sum + stepWeight(ref)
  }, 0)
  return {
    totalWeight,
    completedWeight: 0,
    skippedWeight: 0,
    runningWeight: 0,
    runningName: null,
    runningStage: null,
  }
}

/**
 * Fold a `PIPELINE_STEP` payload into the running weights. Running steps set the
 * in-flight weight/name/stage; skipped steps are excluded from the denominator
 * (they're instant and shouldn't inflate the bar); completed/failed count as
 * done work. Mirrors the detail page's original reducer exactly.
 */
export function applyPipelineStep(prev: IPipelineWeights | null, payload: Record<string, unknown>): IPipelineWeights {
  const status = typeof payload.status === "string" ? payload.status : ""
  const ref = typeof payload.ref === "string" ? payload.ref : ""
  const name = typeof payload.name === "string" ? payload.name : null
  const w = stepWeight(ref)
  const base: IPipelineWeights = prev ?? {
    totalWeight: 0,
    completedWeight: 0,
    skippedWeight: 0,
    runningWeight: 0,
    runningName: null,
    runningStage: null,
  }
  if (status === "running") {
    return {
      ...base,
      runningWeight: w,
      runningName: name,
      runningStage: ref ? stepStage(ref) : base.runningStage,
    }
  }
  if (status === "skipped") {
    return { ...base, skippedWeight: base.skippedWeight + w, runningWeight: 0 }
  }
  return { ...base, completedWeight: base.completedWeight + w, runningWeight: 0 }
}

/**
 * Weighted progress over the steps that actually run (skipped excluded), with
 * 50% credit for the in-flight step so the bar advances when the heavy summary
 * starts and then creeps — instead of jumping to ~90% off skipped steps.
 * `null` when there's no pipeline yet or nothing counts toward the denominator.
 */
export function weightedPct(p: IPipelineWeights | null): number | null {
  if (!p) return null
  const denom = p.totalWeight - p.skippedWeight
  if (denom <= 0) return null
  return Math.min(100, Math.round(((p.completedWeight + 0.5 * p.runningWeight) / denom) * 100))
}

/**
 * Merge the live pipeline weights, the coarse SSE stage/progress, and the
 * persisted baseline into a single render-ready value. The live per-step signal
 * wins for the stage; progress floors to the active stage (and to any weighted /
 * persisted / live number) so a missed or late SSE event never leaves the bar
 * stuck on step 1. Generalized from the detail page's derived block — the extra
 * `sseProgress` term is a no-op when null, keeping the detail page identical.
 */
export function deriveEffectiveProgress(input: {
  pipeline: IPipelineWeights | null
  sseStage: TProcessingStage | null
  sseProgress: number | null
  persistedStage: string | null | undefined
  persistedProgress: number | null | undefined
}): IMeetingCardProcessing {
  const { pipeline, sseStage, sseProgress, persistedStage, persistedProgress } = input
  const persisted: TProcessingStage | null = persistedStage && isProcessingStage(persistedStage) ? persistedStage : null
  const stage: TProcessingStage | null = pipeline?.runningStage ?? sseStage ?? persisted
  const pct = weightedPct(pipeline)
  const progress = Math.max(persistedProgress ?? 0, sseProgress ?? 0, pct ?? 0, stage ? (STAGE_FLOOR[stage] ?? 0) : 0)
  return { stage, progress, currentStepName: pipeline?.runningName ?? null }
}
